import { Component, OnInit } from '@angular/core';
import { BaseTunnelObject } from '../../model/base-tunnel-object';
import { TunnelDataStore } from '../../../../stores/tunnel/tunnel-data.store';
import { TunnelNavigationStore } from '../../../../stores/tunnel/tunnel-navigation.store';
import { CustomerState } from '../../../../stores/tunnel/model/customer-state';
import { Funding } from '../../../../stores/tunnel/model/funding';
import { FundingState } from '../../../../stores/tunnel/model/funding-state';
import { InstallationState } from '../../../../stores/tunnel/model/installation-state';
import { PricingState } from '../../../../stores/tunnel/model/pricing-state';
import { DiscountTypeEnum } from '../../../../enum/discount-type.enum';
import { MODAL, ModalService } from '../../../../services/modal.service';
import { StatisticService } from '../../../../services/statistic.service';
import { StatisticTypeEnum } from '../../../../enum/statistic-type.enum';
import { CONFIG_CONTEXT } from '../../../../enum/config-context.enum';
import { BathroomHbmConfigurationData } from '../../../../stores/tunnel/model/configuration-data/bathroom-hbm-configuration-data';
import { BathroomHbmService } from '../../../../services/configurators-2d/applications/bathroom-hbm.service';
import { FidelityService } from '../../../../services/fidelity/fidelity.service';
import { FeatureSettingsService } from '../../../../services/feature-settings/feature-settings.service';
import {
  FidelityCardPointSimulationDto,
  FidelityCardTypeEnum,
  GetCustomerFidelityInfoInput,
} from '../../../../services/fidelity/fidelity';
import { PaytweakRequestInput } from '../../../../services/fidelity/paytweak';
import { logError } from 'src/app/services/helpers/sentry.helper';

/**
 * Étape « Validation » du tunnel SDB ByMe/HBM 3D.
 *
 * Récapitulatif du projet (total + pose) avant enregistrement, complété par la section fidélité (C'Casto / C'Casto Max).
 */
@Component({
  selector: 'ng-casto-bathroom-hbm-tunnel-validation',
  templateUrl: './bathroom-hbm-tunnel-validation.component.html',
})
export class BathroomHbmTunnelValidationComponent extends BaseTunnelObject implements OnInit {
  protected currentPricing: PricingState;
  protected currentInstallation: InstallationState;
  protected currentCustomer: CustomerState;
  protected currentFunding: FundingState;
  protected currentDiscount: DiscountTypeEnum;
  protected currentWithInstallation: boolean = null;
  protected loading: boolean = false;
  protected fidelityLoading: boolean = false;
  protected refreshLoading: boolean = false;
  private bathroomHbmConfiguration: BathroomHbmConfigurationData;
  protected readonly FidelityCardTypeEnum = FidelityCardTypeEnum;

  constructor(
    protected tunnelDataStore: TunnelDataStore,
    protected tunnelNavigationStore: TunnelNavigationStore,
    private readonly bathroomHbmService: BathroomHbmService,
    private readonly modalService: ModalService,
    private readonly statisticService: StatisticService,
    private readonly fidelityService: FidelityService,
    protected readonly featureSettingsService: FeatureSettingsService
  ) {
    super(tunnelNavigationStore, tunnelDataStore);
  }

  get withInstallation(): boolean {
    return this.currentInstallation?.withInstallation ?? false;
  }

  /** La pose n'est proposée que si le feature setting associé est actif. */
  /** Pose masquée si le feature setting est coupé ou si l'environnement la désactive. */
  get disableInstallation(): boolean {
    return !this.featureSettingsService.poseEnabled || this.bathroomHbmService.isInstallationDisabled();
  }

  /** Offre de financement retenue pour le scénario de pose courant. */
  get funding(): Funding {
    return this.currentFunding?.[this.withInstallation ? 'fundingWithInstallation' : 'fundingWithoutInstallation'];
  }

  get displayDiscount(): boolean {
    return this.discountNumberValue > 0;
  }

  get discountNumberValue(): number {
    switch (this.currentDiscount) {
      case DiscountTypeEnum.Discount0:
        return 0;
      case DiscountTypeEnum.Discount5:
        return 5;
      case DiscountTypeEnum.Discount10:
        return 10;
      case DiscountTypeEnum.Discount20:
        return 20;
    }
  }

  get currentFidelity(): FidelityCardPointSimulationDto {
    if (this.currentPricing?.fidelity) {
      return this.currentPricing.fidelity[this.withInstallation ? 0 : 1];
    }
    return undefined;
  }

  get isCardExpired(): boolean {
    if (!this.currentCustomer.fidelity?.expiredDate) return false;
    let today = new Date();
    let dateToNotice = new Date();

    const nbDaysNoticeEndSubscriptionPaidCard = 30;
    //Ajout de la date de notification en fonction du setting
    dateToNotice.setTime(
      this.currentCustomer.fidelity.expiredDate.getTime() - nbDaysNoticeEndSubscriptionPaidCard * 24 * 60 * 60 * 1000
    );
    return today > dateToNotice;
  }

  get premiumCardEarnedCoupons(): number {
    let count = 0;
    this.currentFidelity?.premiumCardCoupons?.forEach(coupon => {
      count += coupon.count;
    });
    return count;
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.statisticService.sendStatistic(
      StatisticTypeEnum.VALIDATION_PER_PROJECT_COUNT,
      1,
      CONFIG_CONTEXT.BATHROOM,
      this.bathroomHbmConfiguration?.configurationId
    );
    this.computeFidelityPoint().then();
  }

  private async computeFidelityPoint(): Promise<void> {
    if (!this.currentPricing?.totalWithoutFunding?.actualAmount || !this.currentCustomer?.userData) {
      return;
    }
    await this.bathroomHbmService.computeFidelityPoint({
      amount: this.currentPricing.totalWithoutFunding.actualAmount,
      customerId: this.currentCustomer.userData.kfId,
    });
  }

  protected subscribeToObservable() {
    this.subscriptionList.push(
      this.tunnelDataStore.getCurrentPricing().subscribe(currentPricing => {
        if (currentPricing) {
          this.currentPricing = currentPricing;
        }
      }),
      this.tunnelDataStore.getCurrentInstallation().subscribe(currentInstallation => {
        if (currentInstallation) {
          this.currentInstallation = currentInstallation;
        }
      }),
      this.tunnelDataStore.getCurrentWithInstallation().subscribe(async currentWithInstallation => {
        if (currentWithInstallation === undefined || this.currentWithInstallation === currentWithInstallation) {
          return;
        }
        const previousValue = this.currentWithInstallation;
        this.currentWithInstallation = currentWithInstallation;
        // Au premier passage on se contente d'initialiser : le pricing courant est déjà à jour.
        if (previousValue !== null) {
          await this.refreshPricing();
        }
      }),
      this.tunnelDataStore.getCurrentFunding().subscribe(currentFunding => {
        if (currentFunding) {
          this.currentFunding = currentFunding;
        }
      }),
      this.tunnelDataStore.getCurrentDiscount().subscribe(currentDiscount => {
        if (currentDiscount && this.currentDiscount !== currentDiscount) {
          this.currentDiscount = currentDiscount;
        }
      }),
      this.tunnelDataStore.getFidelityLoading().subscribe(fidelityLoading => {
        this.fidelityLoading = fidelityLoading;
      }),
      this.tunnelDataStore.getCurrentCustomer().subscribe(customer => {
        if (customer) {
          this.currentCustomer = customer;
        }
      }),
      this.tunnelDataStore.getConfigurationData().subscribe(configurationData => {
        if (configurationData) {
          this.bathroomHbmConfiguration = configurationData.get(
            CONFIG_CONTEXT.BATHROOM
          ) as BathroomHbmConfigurationData;
        }
      })
    );
  }

  /** Bascule le projet avec/sans pose depuis le récapitulatif. */
  protected async setWithInstallation(withInstallation: boolean): Promise<void> {
    try {
      this.loading = true;
      await this.bathroomHbmService.updateWithInstallation(withInstallation);
    } catch (error) {
      logError(error);
    } finally {
      this.loading = false;
    }
    this.tunnelDataStore.setWithInstallation(withInstallation);
  }

  /**
   * Recalcule le récapitulatif après un changement de scénario de pose.
   * Si le financement est coupé, le service retombe sur un rechargement du pricing.
   */
  private async refreshPricing(): Promise<void> {
    try {
      this.loading = true;
      await this.bathroomHbmService.computeFunding({
        tunnelConfigurationId: this.bathroomHbmConfiguration?.configurationId,
        downPaymentAmount: this.currentFunding?.downPayment,
        shouldRecalculateFinancing: true,
      });
    } catch (error) {
      logError(error);
    } finally {
      this.loading = false;
    }
  }

  /** Ouvre le détail des prestations de pose renvoyées par SPID. */
  openDetail(): void {
    this.modalService.display(MODAL.INSTALLATION_DETAIL, {
      installationDetails: this.currentInstallation?.detailInstallation,
      bom: this.bathroomHbmConfiguration?.bom,
    });
  }

  // #region Fidélité

  protected fidelityEnabled(): boolean {
    return this.featureSettingsService.fidelityEnabled;
  }

  private async createFidelityCard(input: { withPremium: boolean }): Promise<void> {
    if (!(await this.featureSettingsService.isFidelityEnabled())) {
      return;
    }

    this.modalService.display(MODAL.CUSTOMER_INFO_FORM, {
      defaultData: {
        customer: this.currentCustomer.userData,
        address: this.currentCustomer.userData.address,
        marketingConsent: this.currentCustomer.userData.marketingConsent,
      },
      upgrade: input.withPremium,
      statisticType: input.withPremium
        ? StatisticTypeEnum.NEW_PREMIUM_CARD_VALIDATION
        : StatisticTypeEnum.NEW_FREE_CARD_VALIDATION,
    });
  }

  protected async createPremiumCard(): Promise<void> {
    await this.createFidelityCard({ withPremium: true });
  }

  protected async createFreeCard(): Promise<void> {
    await this.createFidelityCard({ withPremium: false });
  }

  private buildPaytweakRequestInput(): PaytweakRequestInput {
    return {
      customerId: this.currentCustomer.userData.kfId,
      cardNumber: this.currentFidelity.cardNumber,
      firstname: this.currentCustomer.userData.firstname,
      lastname: this.currentCustomer.userData.lastname,
      address: this.currentCustomer.userData.address.line1,
      postalCode: this.currentCustomer.userData.address.postalCode,
      city: this.currentCustomer.userData.address.city,
      country: this.currentCustomer.userData.address.country,
      phone: this.currentCustomer.userData.phoneNumber,
      email: this.currentCustomer.userData.email,
    };
  }

  private async showPaytweakQrCode(): Promise<void> {
    if (!(await this.featureSettingsService.isFidelityEnabled())) {
      return;
    }

    this.modalService.display(MODAL.FIDELITY_PAYMENT_QRCODE, {
      paytweakRequestInput: this.buildPaytweakRequestInput(),
      onCloseCallback: undefined,
    });
  }

  private async postRenewPremiumCard(): Promise<void> {
    if (!(await this.featureSettingsService.isFidelityEnabled())) {
      return;
    }

    const input: GetCustomerFidelityInfoInput = {
      idKingfisher: this.currentCustomer.userData.kfId,
    };
    if (this.currentCustomer.userData.kfId != null) {
      try {
        await this.fidelityService.postRenewPremiumCard(input);
      } catch (error) {
        logError(error);
      } finally {
        await this.showPaytweakQrCode();
      }
    }
  }

  protected async updatePremiumCard(): Promise<void> {
    await this.postRenewPremiumCard();
  }

  protected async getPaymentLink(): Promise<void> {
    await this.showPaytweakQrCode();
  }

  protected async refreshFidelity(): Promise<void> {
    try {
      this.refreshLoading = true;
      await this.tunnelDataStore.loadCurrentCustomer(this.currentCustomer.userData);
    } catch (error) {
      logError(error);
    } finally {
      this.refreshLoading = false;
    }
  }

  // #endregion
}
